import { useEffect, useState } from 'react';
import { completeUpload, createUpload, putToR2 } from '../lib/api';
import { processImage } from '../lib/image';
import { CategorySelect } from './CategorySelect';
import { TagInput } from './TagInput';
import { IMAGE_LIMITS } from '../../shared/types';

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
  categorySuggestions: string[];
  tagSuggestions: string[];
}

type Stage = 'idle' | 'compressing' | 'uploading' | 'finalizing' | 'done' | 'error';

export function UploadDialog({
  open,
  onClose,
  onUploaded,
  categorySuggestions,
  tagSuggestions,
}: UploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [stage, setStage] = useState<Stage>('idle');
  const [progress, setProgress] = useState({ thumb: 0, full: 0 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function reset() {
    setFile(null);
    setTitle('');
    setDescription('');
    setCategory('');
    setTags([]);
    setStage('idle');
    setProgress({ thumb: 0, full: 0 });
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !title.trim() || !category.trim()) return;
    setError(null);
    try {
      setStage('compressing');
      const { thumb, full } = await processImage(file);

      setStage('uploading');
      const presigned = await createUpload({
        title: title.trim(),
        description: description.trim() || null,
        category: category.trim(),
        tags,
        thumb: { content_type: thumb.content_type, width: thumb.width, height: thumb.height, size: thumb.size },
        full: { content_type: full.content_type, width: full.width, height: full.height, size: full.size },
      });

      await Promise.all([
        putToR2({
          url: presigned.thumb.url,
          blob: thumb.blob,
          headers: presigned.thumb.required_headers,
          onProgress: (loaded, total) => setProgress((p) => ({ ...p, thumb: loaded / total })),
        }),
        putToR2({
          url: presigned.full.url,
          blob: full.blob,
          headers: presigned.full.required_headers,
          onProgress: (loaded, total) => setProgress((p) => ({ ...p, full: loaded / total })),
        }),
      ]);

      setStage('finalizing');
      await completeUpload(presigned.artwork_id, {
        thumb_width: thumb.width,
        thumb_height: thumb.height,
        full_width: full.width,
        full_height: full.height,
      });

      setStage('done');
      onUploaded();
      reset();
      onClose();
    } catch (err) {
      setStage('error');
      setError((err as Error).message);
    }
  }

  if (!open) return null;
  const busy = stage === 'compressing' || stage === 'uploading' || stage === 'finalizing';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="my-12 w-full max-w-2xl rounded-3xl bg-white p-6 shadow-xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold">Upload artwork</h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            className="rounded-full p-2 text-ink-500 hover:bg-canvas-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="label">Image</label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              {previewUrl ? (
                <div className="h-40 w-40 overflow-hidden rounded-xl border border-ink-900/10 bg-canvas-100">
                  <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="grid h-40 w-40 place-items-center rounded-xl border border-dashed border-ink-900/15 bg-canvas-50 text-xs text-ink-500">
                  No image yet
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setFile(f);
                    if (f && !title) {
                      const noExt = f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
                      setTitle(noExt.charAt(0).toUpperCase() + noExt.slice(1));
                    }
                  }}
                  required
                  className="block w-full text-sm text-ink-700 file:mr-3 file:rounded-full file:border-0 file:bg-ink-900 file:px-4 file:py-2 file:text-sm file:font-medium file:text-canvas-50 hover:file:bg-ink-700"
                />
                <p className="mt-2 text-xs text-ink-500">
                  Max {Math.round(IMAGE_LIMITS.MAX_SOURCE_BYTES / 1024 / 1024)} MB. We'll
                  compress it to WebP automatically — both a small thumbnail and a
                  larger display version. Your original file stays on your device.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="up-title">Title</label>
              <input
                id="up-title"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="up-cat">Category</label>
              <CategorySelect value={category} onChange={setCategory} suggestions={categorySuggestions} />
            </div>
          </div>

          <div>
            <label className="label" htmlFor="up-desc">Description (optional)</label>
            <textarea
              id="up-desc"
              className="input min-h-[6rem]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A sentence or two about the piece"
            />
          </div>

          <div>
            <label className="label">Tags</label>
            <TagInput value={tags} onChange={setTags} suggestions={tagSuggestions} />
          </div>

          {(stage === 'uploading' || stage === 'finalizing') && (
            <div className="space-y-2 rounded-xl bg-canvas-50 p-3 text-xs text-ink-700">
              <ProgressBar label="Thumbnail" pct={progress.thumb} />
              <ProgressBar label="Display image" pct={progress.full} />
              {stage === 'finalizing' && <div>Finalizing…</div>}
            </div>
          )}
          {stage === 'compressing' && (
            <div className="rounded-xl bg-canvas-50 p-3 text-xs text-ink-700">
              Compressing image…
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-accent/30 bg-accent/5 p-3 text-sm text-accent">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => !busy && onClose()}
              disabled={busy}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !file}>
              {busy ? 'Uploading…' : 'Upload artwork'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProgressBar({ label, pct }: { label: string; pct: number }) {
  const w = `${Math.round(pct * 100)}%`;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span>{label}</span>
        <span>{w}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-canvas-200">
        <div className="h-full bg-ink-900 transition-all" style={{ width: w }} />
      </div>
    </div>
  );
}
